/**
 * MapInterpolation — scroll-synchronised camera interpolation for map layers.
 *
 * Given an ordered list of MapWaypoints (each tagged with a scrollPosition),
 * interpolates lng, lat, zoom, bearing and pitch for any timer value.
 * Uses the same Easing functions as the rest of the Scrolly keyframe system.
 */

import { Easings } from "@scrolly/renderer";
import type { PossibleEasings } from "@scrolly/post-types";
import type { MapMarker, MapWaypoint } from "@scrolly/post-types";

export interface InterpolatedCamera {
    lng: number;
    lat: number;
    zoom: number;
    bearing: number;
    pitch: number;
}

/** Lerp that handles longitude wrapping across the ±180° antimeridian. */
function lerpLng(a: number, b: number, t: number): number {
    let delta = b - a;
    // Take the shortest path across ±180
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    let result = a + delta * t;
    // Normalise to [-180, 180]
    if (result > 180) result -= 360;
    if (result < -180) result += 360;
    return result;
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

/**
 * Interpolate camera parameters between waypoints for a given scroll timer.
 *
 * - Before first waypoint: returns first waypoint's values.
 * - After last waypoint: returns last waypoint's values.
 * - Between two waypoints: eased interpolation using the **destination** waypoint's easing.
 */
export function interpolateWaypoints(
    waypoints: MapWaypoint[],
    timer: number,
): InterpolatedCamera {
    if (waypoints.length === 0) {
        return { lng: 0, lat: 0, zoom: 2, bearing: 0, pitch: 0 };
    }
    if (waypoints.length === 1 || timer <= waypoints[0].scrollPosition) {
        const w = waypoints[0];
        return {
            lng: w.lng,
            lat: w.lat,
            zoom: w.zoom,
            bearing: w.bearing ?? 0,
            pitch: w.pitch ?? 0,
        };
    }
    if (timer >= waypoints[waypoints.length - 1].scrollPosition) {
        const w = waypoints[waypoints.length - 1];
        return {
            lng: w.lng,
            lat: w.lat,
            zoom: w.zoom,
            bearing: w.bearing ?? 0,
            pitch: w.pitch ?? 0,
        };
    }

    // Find bracketing waypoints
    let fromIdx = 0;
    for (let i = 1; i < waypoints.length; i++) {
        if (waypoints[i].scrollPosition >= timer) {
            fromIdx = i - 1;
            break;
        }
    }
    const from = waypoints[fromIdx];
    const to = waypoints[fromIdx + 1];

    const range = to.scrollPosition - from.scrollPosition;
    const rawT = range > 0 ? (timer - from.scrollPosition) / range : 1;

    // Apply easing from destination waypoint (default quadInOut)
    const easingKey = (to.easing ?? "quadInOut") as PossibleEasings;
    const easingFn = Easings[easingKey] ?? Easings.quadInOut;
    const t = easingFn(Math.max(0, Math.min(1, rawT)));

    return {
        lng: lerpLng(from.lng, to.lng, t),
        lat: lerp(from.lat, to.lat, t),
        zoom: lerp(from.zoom, to.zoom, t),
        bearing: lerp(from.bearing ?? 0, to.bearing ?? 0, t),
        pitch: lerp(from.pitch ?? 0, to.pitch ?? 0, t),
    };
}

/**
 * Return markers that should be visible at the given timer value.
 * Shows markers from the closest waypoint (by scroll position).
 */
export function getActiveMarkers(
    waypoints: MapWaypoint[],
    timer: number,
): MapMarker[] {
    if (waypoints.length === 0) return [];

    let closest = waypoints[0];
    let minDist = Math.abs(timer - closest.scrollPosition);

    for (let i = 1; i < waypoints.length; i++) {
        const dist = Math.abs(timer - waypoints[i].scrollPosition);
        if (dist < minDist) {
            minDist = dist;
            closest = waypoints[i];
        }
    }

    return closest.markers ?? [];
}
